/**
 * APX RedTeam -- Print / PDF helper
 *
 * Layout:
 *  - Fixed #print-header repeats on every page
 *  - Fixed #print-footer with CSS counter(page)/counter(pages) for page numbers
 *  - 3-column content flows naturally across as many pages as needed
 */

export interface RedTeamResult {
  idea: string;
  sector?: string;
  cfoCritique: string;
  clientCritique: string;
  competitorCritique: string;
  thirdCriticLabel?: string;
  thirdCriticSubtitle?: string;
}

const SECTOR_LABELS: Record<string, string> = {
  general:     '',
  restaurant:  'Restaurant',
  bodega:      'Bodega / Small Market',
  trucking:    'Transportation & Trucking',
  automotive:  'Automotive Repair',
  real_estate: 'Real Estate',
  contractor:  'General Contractor',
  internal:    'Internal / APX Operations',
};

function clean(s: string): string {
  return s.replace(/\*\*/g, '').replace(/\*/g, '').trim();
}

function formatBody(text: string): string {
  const lines = text.split('\n');
  let out = '';
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    out += '<p style="margin:0 0 9px 0;line-height:1.65;">' + trimmed + '</p>';
  }
  return out;
}

function extractHeading(text: string): { heading: string; body: string } {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length > 1 && lines[0].length < 80 && !lines[0].match(/^\d+\./)) {
    return { heading: lines[0], body: lines.slice(1).join('\n').trim() };
  }
  return { heading: '', body: text.trim() };
}

function criticCard(label: string, subtitle: string, text: string, headerBg: string): string {
  const mitigationIdx = text.toLowerCase().indexOf('mitigation:');
  const rawObj = mitigationIdx === -1 ? text.trim() : text.slice(0, mitigationIdx).trim();
  const rawMit = mitigationIdx === -1 ? '' : text.slice(mitigationIdx + 'mitigation:'.length).trim();
  const { heading, body } = extractHeading(clean(rawObj));

  let html = '';
  html += '<td style="vertical-align:top;padding:0;border:1px solid #c0c0c0;">';
  html += '<div style="background:' + headerBg + ';padding:9px 12px 7px;text-align:center;">';
  html += '<div style="font-weight:900;font-size:14px;color:#000000;letter-spacing:0.01em;">' + label + '</div>';
  html += '<div style="font-size:10px;color:#111111;font-style:italic;font-weight:600;margin-top:2px;">' + subtitle + '</div>';
  html += '</div>';
  html += '<div style="padding:11px 11px 13px;background:#fff;">';
  if (heading) {
    html += '<div style="font-weight:700;font-size:12px;color:#111;margin-bottom:8px;line-height:1.4;">' + heading + '</div>';
  }
  html += '<div style="font-size:11.5px;color:#222;">' + formatBody(body) + '</div>';
  if (rawMit) {
    html += '<div style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.08em;color:#0d7a5f;margin:10px 0 5px;">MITIGATION</div>';
    html += '<div style="font-size:11.5px;color:#1a4a3a;">' + formatBody(clean(rawMit)) + '</div>';
  }
  html += '</div>';
  html += '</td>';
  return html;
}

const LOGO_URL = 'https://d2xsxph8kpxj0f.cloudfront.net/310519663412810690/7y9xgY6crGLAQqoXCAMXSi/apx-logo-green-bg_6af79fe5.png';

function buildHeader(dateStr: string, idea: string, sector?: string): string {
  const sectorLabel = sector && sector !== 'general' ? SECTOR_LABELS[sector] ?? sector : '';
  let h = '';
  h += '<div style="display:flex;align-items:center;justify-content:space-between;padding:4px 0 6px;">';
  h += '<div style="display:flex;align-items:center;gap:9px;">';
  h += '<img src="' + LOGO_URL + '" alt="APX" style="width:40px;height:40px;object-fit:contain;border-radius:5px;" />';
  h += '<div>';
  h += '<div style="font-weight:900;font-size:26px;color:#111;line-height:1;letter-spacing:-0.01em;">APX</div>';
  h += '<div style="font-size:8.5px;color:#1e3a5f;margin-top:1px;font-weight:500;">Financial Clarity &amp; Strategic Guidance</div>';
  h += '</div>';
  h += '</div>';
  h += '<div style="font-size:13px;font-weight:700;color:#111;line-height:1;">' + dateStr + '</div>';
  h += '</div>';
  h += '<div style="border-bottom:2px solid #1e3a5f;margin:0;"></div>';
  h += '<div style="background:#1e3a5f;padding:4px 10px;margin:0;">';
  h += '<div style="font-size:8px;font-weight:700;color:#fff;text-align:right;letter-spacing:0.12em;text-transform:uppercase;">Internal Advisory Document</div>';
  h += '</div>';
  h += '<div style="background:#f5f5f5;border-left:4px solid #1e3a5f;padding:10px 18px;margin:6px 0 0;">';
  if (sectorLabel) {
    h += '<div style="font-size:8px;font-weight:700;color:#555;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:5px;">Sector: ' + sectorLabel + '</div>';
  }
  h += '<div style="font-size:13.5px;font-style:italic;font-weight:600;color:#111;line-height:1.5;text-align:center;">';
  h += '&ldquo;' + idea + '&rdquo;';
  h += '</div>';
  h += '</div>';
  return h;
}

export function openPrintWindow(result: RedTeamResult): void {
  const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const thirdLabel    = result.thirdCriticLabel    ?? 'Competitor';
  const thirdSubtitle = result.thirdCriticSubtitle ?? 'Rival firm regional patterns';

  let css = '';
  css += '* { box-sizing: border-box; margin: 0; padding: 0; }';
  css += 'html, body { font-family: Georgia, "Times New Roman", serif; color: #111; background: #fff; font-size: 11.5px; }';
  css += '@page { size: portrait; margin: 3.2cm 0.25in 1.2cm; }';
  css += '#print-header { position: fixed; top: 0; left: 0; right: 0; background: #fff; padding: 0; }';
  css += '#print-footer { position: fixed; bottom: 0; left: 0; right: 0; background: #fff; border-top: 1px solid #ccc; padding: 3px 4px 2px; font-size: 8.5px; color: #777; font-family: Helvetica, Arial, sans-serif; display: flex; justify-content: space-between; align-items: center; }';
  // CSS counter for page numbers -- counter(page) and counter(pages) work on fixed elements in Chrome print
  css += '#print-footer .page-num::after { content: "Page " counter(page) " of " counter(pages); font-weight: 600; color: #333; }';
  css += 'table.cols { width: 100%; border-collapse: collapse; table-layout: fixed; }';
  css += 'table.cols td { vertical-align: top; width: 33.33%; }';

  let html = '';
  html += '<!DOCTYPE html>';
  html += '<html><head>';
  html += '<meta charset="utf-8" />';
  html += '<title>APX RedTeam Stress Test</title>';
  html += '<style>' + css + '</style>';
  html += '</head><body>';
  html += '<div id="print-header">';
  html += buildHeader(dateStr, result.idea, result.sector);
  html += '</div>';
  html += '<div id="print-footer">';
  html += '<span>AI RedTeam &middot; Stress Test &nbsp;|&nbsp; Results are AI-generated and should be reviewed critically before acting on them.</span>';
  html += '<span class="page-num"></span>';
  html += '</div>';
  html += '<table class="cols"><tr>';
  html += criticCard('CFO Skeptic',      'Internal financial risk advisor', result.cfoCritique,        '#c8d8e8');
  html += criticCard('Resistant Client', 'Skeptical small business owner',  result.clientCritique,     '#e8d0c8');
  html += criticCard(thirdLabel,         thirdSubtitle,                     result.competitorCritique, '#dcc8e0');
  html += '</tr></table>';
  // Split script tag to avoid esbuild treating it as a closing tag
  html += '<scr' + 'ipt>(function(){ window.addEventListener("load", function(){ setTimeout(function(){ window.focus(); window.print(); }, 400); }); })();<\/scr' + 'ipt>';
  html += '</body></html>';

  const win = window.open('', '_blank', 'width=900,height=800');
  if (!win) {
    alert('Please allow pop-ups for this site to print.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}
